import { useState } from 'react';
import { toast } from 'sonner';
import { ImageDown, Loader2, Send, FileText } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { shareImage } from '../../lib/play';

/** Share (phone share sheet / download) + Send-to-players buttons for a PNG card. */
export default function CardActions({ buildPath, sendPath, canSend, filename, shareText, sentInfo, compact, pdf }) {
  const [busy, setBusy] = useState(null);

  const share = async (path = buildPath, name = filename, key = 'share') => {
    setBusy(key);
    try {
      const r = await api.post(path);
      const res = await shareImage(r.data.url, r.data.download_name || name || r.data.filename, shareText);
      if (res === 'downloaded') toast.success(`${key === 'pdf' ? 'PDF' : 'Image'} saved — attach it in your WhatsApp group`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create file');
    } finally {
      setBusy(null);
    }
  };

  const send = async (path = sendPath, key = 'send') => {
    setBusy(key);
    try {
      const r = await api.post(path);
      const d = r.data;
      if (d.skipped) toast(`Not sent: ${d.skipped}`);
      else if (d.sent) toast.success(`Sent to ${d.sent} player${d.sent === 1 ? '' : 's'} on WhatsApp${d.failed ? ` · ${d.failed} failed` : ''}`);
      else toast.error(d.errors?.[0] || 'WhatsApp send failed — use Share instead');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${compact ? '' : 'flex-wrap'}`} data-testid="card-actions">
      {pdf && (
        <>
          <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={() => share(pdf.buildPath, pdf.filename, 'pdf')} disabled={!!busy} data-testid="card-pdf-btn">
            {busy === 'pdf' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><FileText className="w-3.5 h-3.5 mr-1" /> Branded PDF</>}
          </Button>
          {canSend && (
            <Button size="sm" variant="outline" className="rounded-full h-8 text-xs border-emerald-600/50 text-emerald-700 dark:text-emerald-300" onClick={() => send(pdf.sendPath, 'pdf-send')} disabled={!!busy} data-testid="card-pdf-send-btn">
              {busy === 'pdf-send' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Send PDF</>}
            </Button>
          )}
        </>
      )}
      <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={() => share()} disabled={!!busy} data-testid="card-share-btn">
        {busy === 'share' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><ImageDown className="w-3.5 h-3.5 mr-1" /> Share image</>}
      </Button>
      {canSend && (
        <Button size="sm" className="rounded-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => send()} disabled={!!busy} data-testid="card-send-btn">
          {busy === 'send' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1" /> Send to players</>}
        </Button>
      )}
      {sentInfo && (
        <span className="text-xs text-muted-foreground" data-testid="card-sent-info">
          {sentInfo.sent ? `Auto-sent to ${sentInfo.sent}${sentInfo.formats ? ` as ${sentInfo.formats.map((f) => (f === 'png' ? 'image' : f.toUpperCase())).join(' + ')}` : ''}` : sentInfo.skipped ? `Auto-send skipped (${sentInfo.skipped})` : sentInfo.failed ? `Auto-send failed (${sentInfo.failed})` : ''}
        </span>
      )}
    </div>
  );
}
